import React, {
  useCallback,
  useEffect,
  useState,
  type FC,
  type PropsWithChildren,
} from 'react';
import { updateGeo } from '@reducers/search';
import { useViewport } from '@sbt-web/hooks';
import { GeoEntry, TuttaItalia } from '@sbt-web/networking';
import { Button, Icon, IconSize } from '@sbt-web/ui';
import { getOrCreatePulse } from '@tools/tracking/utils';
import { useAppDispatch, useAppSelector } from '@reducers/common';

import ownClasses from './NationwideSearchExpander.module.scss';

const searchExpansionTracking = {
  type: 'Click',
  page: {
    get '@id'() {
      return `sdrn:subito:page:${window.location.href}`;
    },
    '@type': 'Page',
    pageType: 'listing',
  },
  target: {
    id: 'listing',
    type: 'Listing',
  },
  object: {
    id: 'shipping-filter-location-expander',
    type: 'UIElement',
    name: 'ShippingFilter',
    get url() {
      return window.location.href;
    },
  },
};

const locationIsAllOfItaly = (location: string) =>
  location === TuttaItalia.value;

const nameFromGeo = (geo: GeoEntry, includeNearbyRegions: boolean): string => {
  const name = geo.town?.value ?? geo.city?.value ?? geo.region.value;

  if (includeNearbyRegions) {
    return `${geo.region.value} e regioni vicine`;
  } else {
    return name;
  }
};

type MessageProps = PropsWithChildren<{
  version: 'cta' | 'thanks';
  id?: string;
}>;

const Message: FC<MessageProps> = ({ version, id, children }) => (
  <div id={id}>
    <div className={ownClasses['search-expansion-prompt']}>
      <div className={ownClasses['bubble']}>
        <Icon
          name={version === 'cta' ? 'Info' : 'Check'}
          iconSize={IconSize.MD}
        />
      </div>

      <div>{children}</div>
    </div>
  </div>
);

type NationwideSearchExpanderProps = {
  id?: string;
};

export const NationwideSearchExpander: FC<NationwideSearchExpanderProps> = ({
  id,
}) => {
  const dispatch = useAppDispatch();

  const includeShippableOnly = useAppSelector(
    ({ search }) => search.includeShippableOnly
  );

  const location = useAppSelector(({ search }) =>
    nameFromGeo(search.geo, search.searchNearRegions ?? false)
  );

  const { isMobile } = useViewport();

  const isInAllOfItaly = locationIsAllOfItaly(location);

  const showSwitch = includeShippableOnly && !isInAllOfItaly;

  const [switchUsed, setSwitchUsed] = useState(false);

  useEffect(() => {
    if (!locationIsAllOfItaly(location) || !includeShippableOnly) {
      setSwitchUsed(false);
    }
  }, [location, includeShippableOnly, setSwitchUsed]);

  const [nonNationalLocation, setNonNationalLocation] = useState<string | null>(
    null
  );

  const expandSearch = useCallback(() => {
    getOrCreatePulse()?.queueEvent(searchExpansionTracking);
    setNonNationalLocation(location);
    setSwitchUsed(true);
    dispatch(updateGeo({ region: TuttaItalia }));
  }, [setNonNationalLocation, location, setSwitchUsed, dispatch]);

  if (switchUsed) {
    if (isMobile) {
      return (
        <Message version="thanks" id={id}>
          <p className={`small book ${isMobile ? 'caption' : 'body-text'}`}>
            Ottimo! Hai cambiato il luogo di ricerca da "{nonNationalLocation}"
            a "Tutta Italia".
          </p>
        </Message>
      );
    } else {
      return null;
    }
  } else if (!showSwitch) {
    return null;
  }

  return (
    <Message version="cta" id={id}>
      <p className={`small book ${isMobile ? 'caption' : 'body-text'}`}>
        Cerca in tutta Italia e non lasciarti scappare l&apos;affare. Con la
        spedizione ricevi l&apos;oggetto che desideri ovunque tu sia.
      </p>

      <Button
        design="text"
        slim
        size="small"
        onClick={expandSearch}
        classes={['search-expander', ownClasses['expand-search']]}
      >
        Cerca in Tutta Italia
      </Button>
    </Message>
  );
};
